import type { AnalysisResult } from '@/hooks/use-homepage-analysis'
import type { DiagnosisResult } from '@/lib/types/diagnosis'

/**
 * The result an anonymous visitor has on screen on `/` or `/diagnose`, kept so
 * a sign-in from any result surface can come back to the SAME result without a
 * new analysis request.
 *
 * Written by the hook whenever a signed-out visitor has a result in state (no
 * surface has to call anything before the OAuth hop). Read once after auth:
 * - on `/result/<analysisId>` (or with that row already in state) it claims the
 *   row onto the new account;
 * - on a `?resultRestore=1` landing it rehydrates the result into state.
 * One-shot and short-lived: the image URL and access token are handoff
 * credentials. Never written while signed in. Dropped on the first signed-in
 * mount of the hook whether or not it matched, after the /timeline claim, and
 * on sign-out; the TTL covers an OAuth round trip and little more.
 */
export const RESULT_HANDOFF_KEY = 'pendingAnalysis_result'
export const RESULT_HANDOFF_TTL = 15 * 60 * 1000
/** The older diagnosis-only handoff (written by persistDiagnosisForOAuth). It
 *  carries the same credentials and is dropped at the same points. */
export const LEGACY_DIAGNOSIS_HANDOFF_KEY = 'pendingAnalysis_diagnosis'
/** Landing marker for an unsaved result. Kept in AuthContext's callback
 *  allowlist so the provider-facing fallback still carries it. */
export const RESULT_RESTORE_PARAM = 'resultRestore'

export interface ResultHandoff {
  savedAt: number
  mode: 'identify' | 'diagnose'
  analysisResult: AnalysisResult | null
  diagnosisResult: DiagnosisResult | null
  imageUrl: string
  supportingImageUrls: string[]
  zipCode: string
  analysisId: string | null
  resultAccessToken: string | null
}

export function saveResultHandoff(state: ResultHandoff): void {
  try {
    localStorage.setItem(RESULT_HANDOFF_KEY, JSON.stringify(state))
  } catch {
    // Storage unavailable: sign-in still works, the result just is not restored.
  }
}

export function clearResultHandoff(): void {
  try {
    localStorage.removeItem(RESULT_HANDOFF_KEY)
  } catch {
    // Storage unavailable.
  }
}

/** Drops both sign-in handoff records (the result and the legacy diagnosis). */
export function clearSignInHandoffs(): void {
  clearResultHandoff()
  try {
    localStorage.removeItem(LEGACY_DIAGNOSIS_HANDOFF_KEY)
  } catch {
    // Storage unavailable.
  }
}

/** Returns a valid, unexpired handoff. Expired or malformed records are dropped. */
export function readResultHandoff(trustsImage: (url: string) => boolean): ResultHandoff | null {
  let state: ResultHandoff | null = null
  try {
    state = JSON.parse(localStorage.getItem(RESULT_HANDOFF_KEY) || 'null') as ResultHandoff | null
  } catch {
    clearResultHandoff()
    return null
  }
  if (!state) return null
  const valid =
    typeof state.savedAt === 'number' &&
    Date.now() - state.savedAt <= RESULT_HANDOFF_TTL &&
    (state.mode === 'identify' || state.mode === 'diagnose') &&
    typeof state.imageUrl === 'string' &&
    trustsImage(state.imageUrl) &&
    Array.isArray(state.supportingImageUrls) &&
    state.supportingImageUrls.every((url) => typeof url === 'string' && trustsImage(url)) &&
    typeof state.zipCode === 'string' &&
    (state.mode === 'identify'
      ? Boolean(state.analysisResult && typeof state.analysisResult.grassType === 'string')
      : Boolean(state.diagnosisResult && typeof state.diagnosisResult === 'object'))
  if (!valid) {
    clearResultHandoff()
    return null
  }
  return state
}
